"use client";

/**
 * spot-prompt.tsx — 出题面:局面(牌桌)+ 问题(decision_point 标题)+ 一行新手提示 + 动作选项
 *
 * 自 /train ActivePhase 纯抽取(零行为变更),供 /train 与 home 首屏共用(禁复制)。
 * 宿主传 scene + chosen(null=未作答)渲染出题面;onChoose(optId) 把选项点击交还宿主,
 * 判答/揭晓/反馈逻辑留在宿主(ActivePhase)。
 */

import { useState } from "react";
import { motion, useReducedMotion } from "motion/react";
import type { Variants } from "motion/react";
import { Check, Lightbulb, X } from "lucide-react";
import type { PilotScene } from "@/lib/scene-types";
import type { DecisionOption } from "@/lib/decision-parser";
import { PokerTable } from "@/components/poker-table";
import { ActionTimeline } from "@/components/action-timeline";
import {
  positionZh,
  streetZh,
  ACTION_EXPLAIN,
  DECISION_SCREEN_GLOSS,
  POSITION_GLOSS,
  SIZING_CUE,
  titleTermHints,
  villainLabelKind,
} from "@/lib/poker-terms-zh";
import { handZh, sanitizeSuitedZh } from "@/lib/poker-cards";
import { jargonZh } from "@/lib/jargon-zh";
import { renderGlossEntries } from "@/components/quiz-gloss";
import { cn } from "@/lib/cn";
import { EASE_OUT } from "@/components/dojo-theme";

export function seededShuffle<T>(arr: T[], seed: string): T[] {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = ((h << 5) - h + seed.charCodeAt(i)) | 0;
  }
  const result = [...arr];
  for (let i = result.length - 1; i > 0; i--) {
    h = (h * 1664525 + 1013904223) | 0;
    const j = ((h >>> 0) % (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

/** 给完全新手的一行「该怎么做」提示 */
const STREET_HINT: Record<string, string> = {
  preflop: "翻前只有两张手牌:先看位置,再看牌力,决定要不要入局。",
  flop: "翻牌亮出三张公共牌:想想你的手牌和牌面有没有关系。",
  turn: "转牌是第四张公共牌:底池更大了,这一步要更谨慎。",
  river: "河牌是最后一张公共牌:没有下一张了,做最后的判断。",
  multi: "这道题跨多条街:跟着行动线,找到最关键的那个决定。",
};
const DEFAULT_HINT = "轮到你了:选一个你认为最好的打法。";

/** 揭晓反馈里的推荐尺度白话(B'④):二元题的尺度不进按钮,在这里一眼呈现 */
const SIZING_PLAIN: Record<string, string> = {
  "1/3": "约底池 1/3（小注）",
  "1/2": "约底池一半",
  "2/3": "约底池 2/3（中注）",
  "3/4": "约底池 3/4（大注）",
  pot: "约整个底池",
  overbet: "超池（比底池还大）",
};

function OptionButton({
  opt,
  street,
  revealed,
  isChosen,
  isRec,
  reduce,
  onChoose,
}: {
  opt: DecisionOption;
  street: string;
  revealed: boolean;
  isChosen: boolean;
  isRec: boolean;
  reduce: boolean;
  onChoose: (id: string) => void;
}) {
  // O:问句问「平跟」而按钮叫「跟注」——翻前的跟注说明直接桥接,消除「对不上」
  // D'③:三档尺寸按钮分档 cue——各档一句「意味什么风险」,替代三档同一句 ACTION_EXPLAIN
  const explain =
    opt.action === "call" && street === "preflop"
      ? "匹配当前下注;翻前只跟入、不加注(平跟)也选这项"
      : opt.action === "bet" && opt.sizing && SIZING_CUE[opt.sizing]
        ? SIZING_CUE[opt.sizing]
        : ACTION_EXPLAIN[opt.action];
  return (
    <button
      type="button"
      disabled={revealed}
      onClick={() => onChoose(opt.id)}
      className={cn(
        "decision-option relative w-full rounded-xl border px-5 py-4 text-left transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#090E0B] [@media(max-height:940px)]:py-3",
        isRec
          ? "border-amber-300/70 bg-[#261E0D] text-amber-100 shadow-[0_0_44px_-10px_rgba(227,181,91,0.45)]"
          : isChosen
            ? "border-rose-400/60 bg-[#271114] text-rose-100"
            : revealed
              ? "border-[#223329] bg-[#0E1712] text-stone-500 opacity-45"
              : "border-[#243528] bg-[#101B14] text-stone-100 hover:-translate-y-0.5 hover:border-emerald-300/40 hover:bg-[#14231A] active:scale-[0.98] active:translate-y-0",
      )}
    >
      {/* 答对的金色脉冲环(一次性) */}
      {isRec && !reduce && (
        <>
          <motion.span
            aria-hidden
            className="pointer-events-none absolute -inset-px rounded-xl border-2 border-amber-300/80"
            initial={{ opacity: 0.7, scale: 1 }}
            animate={{ opacity: 0, scale: 1.5 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          />
          <motion.span
            aria-hidden
            className="pointer-events-none absolute -inset-px rounded-xl border border-amber-200/60"
            initial={{ opacity: 0.6, scale: 1 }}
            animate={{ opacity: 0, scale: 1.85 }}
            transition={{ duration: 0.9, delay: 0.15, ease: "easeOut" }}
          />
        </>
      )}
      <motion.span
        className="flex items-center gap-2.5"
        animate={
          revealed && isChosen && !isRec && !reduce
            ? { x: [0, -7, 7, -4, 4, 0] }
            : revealed && isRec && !reduce
              ? { scale: [1, 1.06, 1] }
              : undefined
        }
        transition={{ duration: 0.4 }}
      >
        <span className="text-base font-semibold">{opt.label}</span>
        {isRec && (
          <span className="flex size-5 items-center justify-center rounded-full bg-amber-300/20 text-amber-300">
            <Check className="size-3.5" />
          </span>
        )}
        {revealed && isChosen && !isRec && (
          <span className="flex size-5 items-center justify-center rounded-full bg-rose-400/20 text-rose-300">
            <X className="size-3.5" />
          </span>
        )}
      </motion.span>
      {explain && (
        <span
          className={cn(
            "option-explain mt-1 block text-[11px] leading-relaxed",
            isRec ? "text-amber-200/60" : isChosen ? "text-rose-200/60" : "text-stone-500",
          )}
        >
          {explain}
        </span>
      )}
    </button>
  );
}

/**
 * 出题面(纯呈现 + 入场动画):局面牌桌 + decision_point 标题 + 新手提示 + 动作选项网格。
 *
 * - chosen:当前选中的选项 id(null = 未作答);揭晓态渲染(金色推荐/红色错选/其余压暗)由它驱动。
 * - onChoose:选项点击回调,判答计时与揭晓逻辑在宿主。
 * - 选项顺序由 scene_id 种子洗牌决定(与宿主判答用的顺序一致);入场窗口内选项不响应点击。
 */
export function SpotPrompt({
  scene,
  chosen,
  onChoose,
}: {
  scene: PilotScene;
  chosen: string | null;
  onChoose: (optId: string) => void;
}) {
  const reduce = useReducedMotion();
  // 入场动画完成前选项不响应点击,防手快误触隐藏态按钮
  const [optsEntered, setOptsEntered] = useState(false);
  const [prevSceneId, setPrevSceneId] = useState(scene.scene_id);

  if (prevSceneId !== scene.scene_id) {
    setPrevSceneId(scene.scene_id);
    setOptsEntered(false);
  }

  const { spot, decision } = scene;
  const useStructured = decision?.parsed === true && decision.options.length >= 2;
  // 概念题(parsed=false)无结构化选项:不派生按钮,避免题目≠按钮(防御层,/train 池已在查询层滤除)
  const options = useStructured ? seededShuffle(decision!.options, scene.scene_id) : [];
  const correctIds = useStructured ? new Set(decision!.correct_ids) : new Set<string>();
  const revealed = chosen !== null;
  // F3:标题渲染管线只走一次——白话不再内联进标题,改复用已有提示行(净增文字为零)
  const titleText = sanitizeSuitedZh(jargonZh(handZh(spot.decision_point)));
  const termHints = titleTermHints(titleText);
  const hint =
    termHints.length > 0
      ? termHints.map((h) => `${h.term} = ${h.gloss}`).join(";")
      : (STREET_HINT[spot.street] ?? DEFAULT_HINT);
  // U1:villain 空不渲「vs 未知位置」——行动线含多人信息 → 多人底池,否则只显示 hero 位置
  const villainKind = villainLabelKind(spot.villain_position, spot.action_line);

  const optionList: Variants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: reduce ? 0 : 0.04,
        delayChildren: reduce ? 0 : 0.12,
      },
    },
  };
  const optionItem: Variants = {
    hidden: { opacity: 0, y: reduce ? 0 : 16 },
    visible: {
      opacity: 1,
      y: 0,
      transition: reduce ? { duration: 0.2 } : { duration: 0.32, ease: EASE_OUT },
    },
  };

  return (
    <>
      {/* 题面随场景重放入场;牌桌不随场景 remount——座位按行动环弹性转动到新方位 */}
      <motion.div
        key={scene.scene_id}
        initial={reduce ? false : { opacity: 0, y: 14, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={
          reduce ? undefined : { type: "spring", stiffness: 240, damping: 26 }
        }
      >
        <div className="flex flex-wrap items-center gap-1.5 text-[11px] font-medium">
          <span className="rounded-md bg-[#173427] px-2 py-0.5 text-[#A7DCC0]">
            {streetZh(spot.street)}
          </span>
          <span className="rounded-md bg-[#16221B] px-2 py-0.5 text-stone-400 ring-1 ring-white/5">
            {renderGlossEntries(positionZh(spot.hero_position), POSITION_GLOSS)}
            {villainKind === "villain" && (
              <>
                {" vs "}
                {renderGlossEntries(positionZh(spot.villain_position), POSITION_GLOSS)}
              </>
            )}
            {villainKind === "multiway" && " · 多人底池"}
          </span>
          {revealed && useStructured && !decision!.gradable && (
            <span className="rounded-md bg-white/5 px-2 py-0.5 text-stone-400">
              方向题 · 不判尺度
            </span>
          )}
          {revealed && useStructured && decision!.sizing && (decision!.primary_action === "bet" || decision!.primary_action === "raise") && (
            <span className="rounded-md bg-amber-300/15 px-2 py-0.5 font-semibold text-amber-200">
              推荐下注 · {SIZING_PLAIN[decision!.sizing] ?? decision!.sizing}
            </span>
          )}
        </div>

        {/* O + 位置名 + D' P0 缺词:点词白话复用 GlossChip;C' 同花XX 先消毒成「同花色手牌XX」再挂载;
            F3 题眼术语白话迁提示行(标题不内联、保持紧凑),标题上的术语 chip 挂载照常深挖 */}
        <h2 className="mt-3 text-balance text-[26px] font-bold leading-snug text-stone-50 md:text-[30px] [@media(max-height:940px)]:mt-2 [@media(max-height:940px)]:text-[24px] [@media(max-height:940px)]:md:text-[27px]">
          {renderGlossEntries(titleText, DECISION_SCREEN_GLOSS)}
        </h2>
      </motion.div>

      {/* 局面:牌桌是视觉化的「局」——你在桌底、对手在哪、牌面是什么;下方行动线讲这手牌的来路;
          矮视口(≤860px 高)行动线折叠+桌面距收紧,把垂直预算让给行动按钮(r4 契约 item 17) */}
      <div className="mt-4 [@media(max-height:940px)]:mt-2.5">
        <PokerTable spot={spot} compact />
        {spot.action_line && (
          <div className="mt-3 [@media(max-height:940px)]:hidden">
            <ActionTimeline actionLine={spot.action_line} heroPosition={spot.hero_position} />
          </div>
        )}
      </div>

      {/* 该怎么做:一行新手提示 */}
      <p
        className="decision-hint flex items-start gap-2 text-sm leading-relaxed text-stone-400"
        data-actions={options.length}
      >
        <Lightbulb className="mt-0.5 size-4 shrink-0 text-amber-300/80" />
        <span>{hint}</span>
      </p>

      {/* 选项:牌桌上的动作按钮(入场窗口内 pointer-events 关闭,防误触) */}
      {useStructured ? (
        <motion.div
          key={`opts-${scene.scene_id}`}
          className={cn("decision-options grid gap-3 sm:grid-cols-2", !optsEntered && "pointer-events-none")}
          role="group"
          aria-label="选择你的打法"
          variants={optionList}
          initial="hidden"
          animate="visible"
        >
          {options.map((opt, i) => (
            <motion.div
              key={opt.id}
              variants={optionItem}
              onAnimationComplete={
                i === options.length - 1 ? () => setOptsEntered(true) : undefined
              }
            >
              <OptionButton
                opt={opt}
                street={spot.street}
                revealed={revealed}
                isChosen={chosen === opt.id}
                isRec={revealed && correctIds.has(opt.id)}
                reduce={reduce ?? false}
                onChoose={onChoose}
              />
            </motion.div>
          ))}
        </motion.div>
      ) : (
        <p className="rounded-xl border border-amber-300/30 bg-[#1B1508]/60 px-4 py-3 text-sm leading-relaxed text-amber-100/90">
          此为概念题(尚未拆成可交互决策题),没有可选打法——看讲解即可。
        </p>
      )}
    </>
  );
}
